import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Pages as PagesIcon,
  HomeRepairService as ServicesIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
  Upload as UploadIcon,
  InsertDriveFile as DocumentIcon,
  Receipt as ReceiptIcon,
  ThreeP as ThreePIcon,
  Article as ArticleIcon,
  Straighten as ComplianceIcon,
  Policy as LegalIcon,
  CurrencyRupee as GSTIcon,
  Assignment as TaskIcon,
} from "@mui/icons-material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const dashboards = [
  { id: 1, name: "Reporting", href: "/Dashboard" },
  { id: 2, name: "Finance", href: "/Dashboard" },
  { id: 3, name: "Admin", href: "/Dashboard" },
  { id: 4, name: "Hr", href: "/Dashboard" },
  { id: 5, name: "MIS", href: "/Dashboard" },
  { id: 6, name: "Marketing", href: "/Dashboard" },
  { id: 7, name: "KPI", href: "/Dashboard" },
  { id: 8, name: "SAAS", href: "/Dashboard" },
  { id: 9, name: "Travel and Tourism", href: "/Dashboard" },
  { id: 10, name: "Hospitality", href: "/Dashboard" },
  { id: 11, name: "BPO", href: "/Dashboard" },
  { id: 12, name: "KPO", href: "/Dashboard" },
];

const Services = [
  { id: 1, category: "Accounting", href: "/Dashboard" },
  { id: 2, category: "Bookkeeping", href: "/Dashboard" },
  { id: 3, category: "Payroll", href: "/Dashboard" },
  { id: 4, category: "Tax Planning", href: "/Dashboard" },
  { id: 5, category: "Financial Reporting", href: "/Dashboard" },
  { id: 6, category: "Audit", href: "/Dashboard" },
  { id: 7, category: "Due Diligence", href: "/Dashboard" },
  { id: 8, category: "Compliances", href: "/Dashboard" },
];

const appsPages = [
  {
    category: "Account",
    apps: [
      { name: "Profile" },
      { name: "Billing" },
      { name: "Security" },
      { name: "Notifications" },
    ],
  },
];

const customisationPages = [{ name: "Reports" }, { name: "Automation" }];

const accoutingPages = [
  { id: 1, name: "Journal Posting", href: "/journalposting" },
  { id: 2, name: "Bank Statement", href: "/bankstatement" },
  { id: 3, name: "Ledger Setup", href: "/ledgersetup" },
];

const Sidebar = ({ forceCollapsed = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const userRole = useSelector((state) => state.auth.user?.role);

  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    } else if (isSmallScreen || isMobile) {
      setCollapsed(true);
    } else if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [forceCollapsed, isSmallScreen, isMobile, isTablet]);

  const handleDrawerToggle = () => {
    if (!isMobile && !forceCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const handleSubMenuClick = (index) => {
    if (!collapsed) {
      setActiveSubMenu(activeSubMenu === index ? null : index);
    }
  };

  const drawerWidth = collapsed ? 72 : 280;

  const menuItems = [
    {
      id: 1,
      title: "Dashboard",
      icon: <DashboardIcon />,
      items: [
        { name: "All", path: "/Dashboard" },
        ...dashboards.map((dashboard) => ({
          name: dashboard.name,
          path: `/Dashboard`,
        })),
      ],
    },
    {
      id: 2,
      title: "Services",
      icon: <PagesIcon />,
      items: Services.map((service) => ({
        name: service.category,
        path: `/Dashboard`,
      })),
    },
    {
      id: 3,
      title: "App Pages",
      icon: <ServicesIcon />,
      items: appsPages.map((appPage) => ({
        category: appPage.category,
        apps: appPage.apps.map((app) => ({
          name: app.name,
          path: `accountprofile?page=${encodeURIComponent(app.name)}`,
        })),
      })),
      hasSubItems: true,
    },
    {
      id: 4,
      title: "Customisation",
      icon: <ServicesIcon />,
      items: customisationPages.map((pages) => ({
        name: pages.name,
        path: `/Dashboard`,
      })),
    },
    {
      id: 5,
      title: "Accounting",
      icon: <ServicesIcon />,

      items: accoutingPages.map((a) => ({
        name: a.name,
        path: a.href,
      })),
    },
  ];

  const singleItems = [
    { title: "Uploads", icon: <UploadIcon />, path: "/upload" },
    { title: "Documents", icon: <DocumentIcon />, path: "/userDocument" },
    {
      title: "Invoice Management",
      icon: <ReceiptIcon />,
      path: "/invoicemanagement",
    },

    // { title: "UserTasks", icon: <ThreePIcon />, path: "/UserTasks" },
    { title: "kycShare", icon: <ThreePIcon />, path: "/KycShare" },

    { title: "Compliance", icon: <ComplianceIcon />, path: "/compliance" },

    { title: "GST", icon: <GSTIcon />, path: "/GstPortal" },
    { title: "Esign", icon: <TaskIcon />, path: "/EsignDashboard" },
    ...(userRole === "admin"  
  ? [{ title: "Super Admin", icon: <SupervisorAccountIcon/>, path: "/superAdmin" }] 
  : []),
    ...(userRole === "admin" || userRole === "ca"
      ? [{ title: "CA Portal", icon: <ThreePIcon />, path: "/Cadashboard" }]
      : []),
    ...(userRole === "admin" || userRole === "cs"
      ? [{ title: "CS Portal", icon: <ArticleIcon />, path: "/csdashboard" }]
      : []),
    ...(userRole === "admin" || userRole === "lawyer"
      ? [{ title: "Legal", icon: <LegalIcon />, path: "/Legal" }]
      : []),
  ];

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      <ListItemButton
        onClick={() => handleMenuClick(item.id)}
        selected={activeMenu === item.id}
        sx={{
          minHeight: 48,
          px: collapsed ? 1.5 : 2.5,
          margin: "4px 16px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "#FFF6E9",
            transform: "translateX(4px)",
            "& .MuiListItemText-primary": {
              color: "#EB5A3C",
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: "#EB5A3C",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 3,
            justifyContent: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Tooltip
            title={item.title}
            placement="right"
            disableHoverListener={!collapsed}
          >
            {item.icon}
          </Tooltip>
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
            {activeMenu === item.id ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowDown />
            )}
          </>
        )}
      </ListItemButton>

      {!collapsed && (
        <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem, index) => (
              <Box key={index}>
                {item.hasSubItems ? (
                  <>
                    <ListItemButton
                      sx={{
                        pl: 4,
                        margin: "2px 16px",
                        borderRadius: "8px",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#FFF6E9",
                          transform: "translateX(4px)",
                          "& .MuiListItemText-primary": {
                            color: "#EB5A3C",
                            fontWeight: "bold",
                          },
                        },
                      }}
                      onClick={() => handleSubMenuClick(index)}
                    >
                      <ListItemText
                        primary={subItem.category}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.8rem",
                            fontWeight: subItem.apps ? 600 : 400,
                          },
                        }}
                      />
                      {subItem.apps &&
                        (activeSubMenu === index ? (
                          <KeyboardArrowRight />
                        ) : (
                          <KeyboardArrowDown />
                        ))}
                    </ListItemButton>
                    {subItem.apps && (
                      <Collapse
                        in={activeSubMenu === index}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {subItem.apps.map((app, appIndex) => (
                            <ListItemButton
                              key={appIndex}
                              component={Link}
                              to={`/accountprofile?page=${encodeURIComponent(
                                app.name
                              )}`}
                              sx={{
                                pl: 6,
                                margin: "2px 16px",
                                borderRadius: "8px",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  backgroundColor: "#FFF6E9",
                                  transform: "translateX(4px)",
                                  "& .MuiListItemText-primary": {
                                    color: "#EB5A3C",
                                    fontWeight: "bold",
                                  },
                                },
                              }}
                            >
                              <ListItemText
                                primary={app.name}
                                sx={{
                                  "& .MuiListItemText-primary": {
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </>
                ) : (
                  <ListItemButton
                    component={Link}
                    to={subItem.path}
                    sx={{
                      pl: 4,
                      margin: "2px 16px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "#FFF6E9",
                        transform: "translateX(4px)",
                        "& .MuiListItemText-primary": {
                          color: "#EB5A3C",
                          fontWeight: "bold",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={subItem.name}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </ListItemButton>
                )}
              </Box>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );

  const renderSingleItem = (item) => (
    <ListItemButton
      key={item.title}
      component={Link}
      to={item.path}
      sx={{
        minHeight: 48,
        px: collapsed ? 1.5 : 2.5,
        margin: "4px 16px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "#FFF6E9",
          transform: "translateX(4px)",
          "& .MuiListItemText-primary": {
            color: "#EB5A3C",
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: "#EB5A3C",
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 3,
          justifyContent: "center",
          color: theme.palette.primary.main,
        }}
      >
        <Tooltip
          title={item.title}
          placement="right"
          disableHoverListener={!collapsed}
        >
          {item.icon}
        </Tooltip>
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.title}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />
      )}
    </ListItemButton>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
            }}
          >
            Dashboard
          </Typography>
        )}
        {!isMobile && !forceCollapsed && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>
          {menuItems.map(renderMenuItem)}
          {singleItems.map(renderSingleItem)}
        </List>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default Sidebar;
